// dBlank/_src/scripts/services/google-maps.js
// This function loads the Google Maps JavaScript API with the specified options.
// It uses the importLibrary method to load the necessary libraries and returns a promise
// that resolves when the API is ready to use.

/**
 * Loads the Google Maps JavaScript API with the specified options.
 * @param {Object} options - The options for loading the Google Maps API.
 * @returns {Promise} A promise that resolves when the Google Maps API is loaded.
 * @example
 * const maps = loadGoogleMapsAPI({
 *   key: 'YOUR_API_KEY',
 *   v: 'weekly',
 *   // Additional options can be added as needed.
 * });
 * maps.importLibrary('places').then(({ PlacesService }) => {
 *   // Use the PlacesService here.
 * });
 * // or
 * const { Map } = await loader.importLibrary('maps');
 * @see {@link https://developers.google.com/maps/documentation/javascript/overview|Google Maps JavaScript API documentation}
 * @see {@link https://developers.google.com/maps/documentation/javascript/libraries|Google Maps libraries documentation}
 */
export function loadGoogleMapsAPI(options) {
  const p = 'The Google Maps JavaScript API';
  const c = 'google';
  const l = 'importLibrary';
  const q = '__ib__';
  const m = document;
  const b = window;
  const d = (b[c] ||= {});
  const maps = (d.maps ||= {});
  const r = new Set();
  const e = new URLSearchParams();
  let h, a, k;

  const u = () =>
    h ||
    // eslint-disable-next-line no-async-promise-executor
    (h = new Promise(async (resolve, reject) => {
      a = m.createElement('script');
      e.set('libraries', [...r] + '');
      for (k in options) {
        e.set(
          k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()),
          options[k],
        );
      }
      e.set('callback', `${c}.maps.${q}`);
      a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
      maps[q] = resolve;
      a.onerror = () => (h = reject(Error(p + ' could not load.')));
      a.nonce = m.querySelector('script[nonce]')?.nonce || '';
      m.head.append(a);
    }));

  if (maps[l]) {
    console.warn(p + ' only loads once. Ignoring:', options);
  } else {
    maps[l] = (f, ...n) => {
      r.add(f);
      return u().then(() => maps[l](f, ...n));
    };
  }

  return maps;
}
